<script lang="ts">
  import type { Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { mdiEmailCheckOutline, mdiSkipNext } from "@mdi/js"
  import StepActionCTA from "../../../../../../../../../_shared/_modules/step-action-cta"
  import type {
    ConfirmationMailActionPhase,
    ConfirmationMailActionResult,
    ConfirmationMailIntent,
    ConfirmationMailIntents,
    ConfirmationMailOperations,
  } from "../../actions/confirmationMail"

  type Props = {
    children?: Snippet<[Snippet, Snippet]>
    intents: ConfirmationMailIntents
    operations: ConfirmationMailOperations
    onBack: () => void
    onSubmissionResult: (result: ConfirmationMailActionResult) => Promise<void> | void
    onPhaseChange?: (phase: ConfirmationMailActionPhase) => void
  }

  const {
    children = null,
    intents,
    operations,
    onBack,
    onSubmissionResult,
    onPhaseChange = () => {},
  }: Props = $props()

  let pendingKind = $state<"skip" | "send" | null>(null)

  const emitResult = async (result: ConfirmationMailActionResult, committed: boolean) => {
    try {
      await onSubmissionResult(result)
    } catch (error) {
      console.error("confirm_appointments_mail_completion_failed", error)
      newNotification({
        class: "error",
        html: committed
          ? "Änderung gespeichert, Oberfläche konnte nicht aktualisiert werden."
          : "Der Schritt konnte nicht fortgesetzt werden.",
      })
    }
  }

  const run = async (intent: ConfirmationMailIntent) => {
    if (pendingKind) return

    if (intent.kind === "blocked") {
      newNotification({ class: "error", html: intent.message })

      return
    }

    pendingKind = intent.kind
    onPhaseChange(intent.kind === "send" ? "sending" : "skipping")
    try {
      if (intent.kind === "skip") {
        await emitResult({ kind: "completed", output: { items: [], skippedByUser: true } }, false)

        return
      }

      const response = await operations.send(intent.eventIds)
      await emitResult(
        { kind: "completed", output: { items: response.items, skippedByUser: false } },
        true,
      )
    } catch (error) {
      console.error("confirm_appointments_mail_failed", error)
      newNotification({
        class: "error",
        html: "Bestätigungsmails konnten nicht vorbereitet werden.",
      })
    } finally {
      pendingKind = null
      onPhaseChange("idle")
    }
  }
</script>

{#snippet primaryAction()}
  <StepActionCTA
    type="primaryButton"
    icon={mdiEmailCheckOutline}
    content={pendingKind === "send" ? "Wird vorbereitet..." : intents.send.label}
    disabled={Boolean(pendingKind) || intents.send.disabled}
    disabledReason={pendingKind
      ? "Bestätigungsmails werden gerade vorbereitet."
      : intents.send.disabledReason}
    onClick={() => run(intents.send)}
  />
{/snippet}

{#snippet footerActions()}
  <div class="confirmation-mail-actions">
    <CTA type="secondaryButton" content="Zurück" disabled={Boolean(pendingKind)} onClick={onBack} />
    <StepActionCTA
      type="secondaryButton"
      icon={mdiSkipNext}
      content={pendingKind === "skip" ? "Wird fortgesetzt…" : intents.skip.label}
      disabled={Boolean(pendingKind)}
      disabledReason={pendingKind ? "Bestätigungsmails werden gerade vorbereitet." : null}
      onClick={() => run(intents.skip)}
    />
    {@render primaryAction()}
  </div>
{/snippet}

{#if children}
  {@render children(primaryAction, footerActions)}
{:else}
  {@render footerActions()}
{/if}

<style>
  .confirmation-mail-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
</style>
